// useCharacter.ts - Updated to use database service
import { useState, useEffect } from "react";
import { Character } from "./types";
import { defaultCharacter } from "./defaultCharacter";
import { databaseService } from "./databaseService";

export const useCharacter = () => {
	const [character, setCharacter] = useState<Character>(defaultCharacter);
	const [currentCharacterId, setCurrentCharacterId] = useState<number | null>(
		null
	);
	const [characters, setCharacters] = useState<
		{
			id: number;
			name: string;
			level: number;
			class: string;
			race: string;
			updatedAt: string;
		}[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Initialize database and load characters
	useEffect(() => {
		const initDatabase = async () => {
			try {
				await databaseService.init();
				await loadCharactersList();
				setIsLoading(false);
			} catch (err) {
				setError(
					"Failed to initialize database: " + (err as Error).message
				);
				setIsLoading(false);
			}
		};

		initDatabase();
	}, []);

	// Load list of all characters
	const loadCharactersList = async () => {
		try {
			const charactersList = await databaseService.getAllCharacters();
			setCharacters(charactersList);
		} catch (err) {
			setError("Failed to load characters: " + (err as Error).message);
		}
	};

	// Load a specific character
	const loadCharacter = async (id: number) => {
		try {
			setIsLoading(true);
			const loadedCharacter = await databaseService.getCharacter(id);
			if (loadedCharacter) {
				setCharacter(loadedCharacter);
				setCurrentCharacterId(id);
			} else {
				setError("Character not found");
			}
		} catch (err) {
			setError("Failed to load character: " + (err as Error).message);
		} finally {
			setIsLoading(false);
		}
	};

	// Save current character (create new or update existing)
	const saveCharacter = async () => {
		try {
			setIsLoading(true);
			let id: number;

			if (currentCharacterId) {
				// Update existing character
				await databaseService.updateCharacter(
					currentCharacterId,
					character
				);
				id = currentCharacterId;
			} else {
				// Create new character
				id = await databaseService.saveCharacter(character);
				setCurrentCharacterId(id);
			}

			// Refresh characters list
			await loadCharactersList();
			return id;
		} catch (err) {
			setError("Failed to save character: " + (err as Error).message);
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	// Create a new character
	const createNewCharacter = () => {
		setCharacter(defaultCharacter);
		setCurrentCharacterId(null);
	};

	// Delete a character
	const deleteCharacter = async (id: number) => {
		try {
			await databaseService.deleteCharacter(id);
			if (currentCharacterId === id) {
				// If we deleted the current character, reset to default
				createNewCharacter();
			}
			await loadCharactersList();
		} catch (err) {
			setError("Failed to delete character: " + (err as Error).message);
			throw err;
		}
	};

	// Deep-update helper (unchanged)
	const updateCharacter = (path: string, value: any) => {
		setCharacter((prev) => {
			// shallow-clone root
			const next = { ...prev };
			const keys = path.split(".");
			let cursor: any = next;

			// drill down, cloning each level
			for (let i = 0; i < keys.length - 1; i++) {
				const key = keys[i];
				cursor[key] = { ...cursor[key] };
				cursor = cursor[key];
			}

			// set the final property
			cursor[keys[keys.length - 1]] = value;
			return next;
		});
	};

	const addItem = (category: keyof Character, item: any) => {
		setCharacter((prev) => ({
			...prev,
			[category]: [...(prev[category] as any[]), item],
		}));
	};

	const removeItem = (category: keyof Character, index: number) => {
		setCharacter((prev) => ({
			...prev,
			[category]: (prev[category] as any[]).filter((_, i) => i !== index),
		}));
	};

	// Legacy export function (for backward compatibility)
	const exportCharacter = () => {
		try {
			const dataStr = JSON.stringify(character, null, 2);
			const dataBlob = new Blob([dataStr], { type: "application/json" });
			const url = URL.createObjectURL(dataBlob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `${character.name || "character"}-dnd-sheet.json`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
		} catch (error) {
			setError("Error exporting character: " + (error as Error).message);
		}
	};

	// Legacy import function (for backward compatibility)
	const importCharacter = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const importedData = JSON.parse(e.target?.result as string);
				// Merge with default character to ensure all fields exist
				const mergedCharacter = {
					...defaultCharacter,
					...importedData,
				};
				setCharacter(mergedCharacter);
				setCurrentCharacterId(null); // This will be a new character when saved
			} catch (error) {
				setError("Error importing character: Invalid file format");
			}
		};
		reader.readAsText(file);
		// Reset file input
		event.target.value = "";
	};

	// Clear error
	const clearError = () => setError(null);

	return {
		// Character data
		character,
		currentCharacterId,
		characters,
		isLoading,
		error,

		// Character operations
		updateCharacter,
		addItem,
		removeItem,

		// Database operations
		loadCharacter,
		saveCharacter,
		createNewCharacter,
		deleteCharacter,
		loadCharactersList,

		// Legacy operations
		exportCharacter,
		importCharacter,

		// Utility
		clearError,
	};
};
